import { useEffect, useState } from "react";
import { FaChartPie, FaTimes } from "react-icons/fa";
import { Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { BACKEND } from "../config/configEnvs";
import { toast } from "react-toastify"
import { useSelector } from "react-redux";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from '@nivo/pie'
import SearchBar from "../components/ui/components/SearchBar";
import Selector from "../components/ui/components/Selector";
import { months, years } from "../utils/rawData/dateValues";
import ServicesCategories from "../utils/plainValues/ServicesCategories";

const EstadisticaIngresos = () => {

  const user = useSelector(state => state.auth.user);

  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [loadingQuotations, setLoadingQuotations]  = useState(false);

  const [brandName, setBrandName] = useState("");

  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  // Función para calcular inicio y fin del mes basado en mes y año seleccionados
  const getStartAndEndOfMonthByYearMonth = (monthValue, yearValue) => {
    const firstDay = new Date(yearValue, monthValue - 1, 1);
    const lastDay = new Date(yearValue, monthValue, 0);
    
    return {
      start: firstDay.toISOString().split("T")[0],
      end: lastDay.toISOString().split("T")[0]
    };
  };

  const [record, setRecord] = useState([]); // Clientes con pagos (de getRecord)
  const [servicesCotizaciones, setServicesCotizaciones] = useState([]); // Servicios de cotizaciones (de getData)
  const [services, setServices] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState("all");

  const getData = async () => {
    try {
      setLoadingServices(true);
      const { start, end } = getStartAndEndOfMonthByYearMonth(month, year);
      let url = `${BACKEND}/api/serviciosCotizaciones?where[and][0][cotizacion.fechaVenta][greater_than_equal]=${start}&where[and][1][cotizacion.fechaVenta][less_than_equal]=${end}&where[and][2][cotizacion.estado][equals]=Exito&limit=0&depth=1&sort=-cotizacion.fechaVenta`;
      
      if (brandName) {
        url += `&where[and][3][cotizacion.cliente.nombreComercial][equals]=${brandName}`;
      }

      const response = await axios.get(url);
      console.log(response)

      setServicesCotizaciones(response.data.docs);
    } catch (error) {
      toast.error("Error al obtener los datos: " + error);
    } finally {
      setLoadingServices(false);
    }
  }

  const getRecord = async () => {
    try {
      setLoadingPayments(true);
      let url = `${BACKEND}/api/getMonthlyIncomes?userId=${user.id}`;

      if (brandName) {
        url += `&brandName=${brandName}`;
      }
      if (month) {
        url += `&month=${month}`;
      }
      if (year) {
        url += `&year=${year}`;
      }
      const res = await axios.get(url);

      // Filter by payment status
      let filteredClients = res.data.clients;
      if (paymentStatus !== "all") {
        filteredClients = filteredClients.filter(client => {
          const nonPayed = client.pagos.filter((p) => p.pagado === false).reduce((acc, p) => acc + p.precio, 0);
          return paymentStatus === "paid" ? nonPayed === 0 : nonPayed > 0;
        });
      }

      setRecord(filteredClients);
    } catch (error) {
      toast.error("Error al obtener los datos");
      console.log(error);
    } finally {
      setLoadingPayments(false);
    }
  }

  const getQuotations = async () => {
    try {
      setLoadingQuotations(true);
      const { start, end } = getStartAndEndOfMonthByYearMonth(month, year);
      let url = `${BACKEND}/api/cotizaciones?where[and][0][fechaVenta][greater_than_equal]=${start}&where[and][1][fechaVenta][less_than_equal]=${end}&limit=0&depth=1&sort=-fechaVenta&where[estado][equals]=Exito&where[primeraCotizacion][equals]=true`;
      
      if (brandName) {
        url += `&where[and][3][cliente.nombreComercial][equals]=${brandName}`;
      }

      const response = await axios.get(url);
      setQuotations(response.data.docs);
    } catch (error) {
      toast.error("Error al obtener los datos");
      console.log(error);
    } finally {
      setLoadingQuotations(false);
    }
  }

  const getServices = async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/getServices`);
      setServices(r.data.services);
    } catch (error) {
      console.log(error);
    }
  }

  const handleResetSearch = () => {
    setBrandName("");
  }

  const handleSearch = () => {
    getData();
  }

  useEffect(() =>{ 
    getServices();
    getData();
    getRecord();
    getQuotations();
  }, []);
  
  useEffect(() => {
    if (brandName === "") {
      getData();
      getRecord();
      getQuotations();
    }
  }, [brandName]); 

  useEffect(() => {
    getData();
    getRecord();
    getQuotations();
  }, [month, year, paymentStatus]);


  const prepareDailyChartData = () => {
    if (!servicesCotizaciones || servicesCotizaciones.length === 0) return [];

    // Group services by date and type
    const servicesByDate = {};
    
    servicesCotizaciones.sort((a, b) => new Date(a.cotizacion.fechaVenta) - new Date(b.cotizacion.fechaVenta)).forEach(item => {
      const date = new Date(item.cotizacion.fechaVenta).toLocaleDateString();
      const serviceType = item?.servicio?.name;
      
      if (!servicesByDate[date]) {
        servicesByDate[date] = {};
      }
      
      if (!servicesByDate[date][serviceType]) {
        servicesByDate[date][serviceType] = 0;
      }
      
      servicesByDate[date][serviceType]++;
    });

    // Transform data for the chart
    return Object.entries(servicesByDate).map(([date, services]) => ({
      fecha: date,
      ...services
    }));
  };

  // Get unique service types for the chart keys
  const getServiceTypes = () => {
    if (!servicesCotizaciones || servicesCotizaciones.length === 0) return [];
    
    const serviceTypes = new Set();
    servicesCotizaciones.forEach(item => {
      serviceTypes.add(item?.servicio?.name);
    });
    
    return Array.from(serviceTypes);
  };

  const preparePaymentsData = () => {
    if (!record || record.length === 0) return [];

    // Group payments by date and service
    const paymentsByDate = {};
    
    record.forEach(cliente => {
      cliente.pagos.forEach(payment => {
        const fechaLimite = payment.fechaLimite;
        if (!fechaLimite) return;
        
        const date = new Date(fechaLimite).toLocaleDateString();
        
        // Process each service amount in the payment
        if (payment?.montosServicios && Array.isArray(payment.montosServicios)) {
          payment.montosServicios.forEach(s => {
            const serviceId = s?.servicioId?.servicio;
            if (!serviceId) return;

            if (!paymentsByDate[date]) {
              paymentsByDate[date] = {};
            }
            
            if (!paymentsByDate[date][serviceId]) {
              paymentsByDate[date][serviceId] = 0;
            }
            
            paymentsByDate[date][serviceId] += s?.monto || 0;
          });
        }
      });
    });

    // Transform data for the chart
    return Object.entries(paymentsByDate)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .map(([date, services]) => ({
        fecha: date,
        ...services
      }));
  };

  const getPaymentServiceTypes = () => {
    if (!record || record.length === 0) return [];
    
    const serviceTypes = new Set();
    record.forEach(cliente => {
      cliente.pagos.forEach(payment => {
        if (payment?.montosServicios && Array.isArray(payment.montosServicios)) {
          payment.montosServicios.forEach(s => {
            const serviceId = s?.servicioId?.servicio;
            if (serviceId) {
              serviceTypes.add(serviceId);
            }
          });
        }
      });
    });
    
    return Array.from(serviceTypes);
  };

  // Obtener categorías únicas y calcular montos por categoría (igual que IngresosMensuales)
  const categorias = ServicesCategories.map((c) => c.value);

  // Calcular montos por categoría con IGV (igual que IngresosMensuales pero con IGV)
  const montosPorCategoria = {};
  categorias.forEach(categoria => {
    montosPorCategoria[categoria] = {
      total: 0,
      igvTotal: 0
    };
    
    if (!record || !Array.isArray(record)) return;
    
    record.forEach(cliente => {
      if (!cliente || !cliente.pagos || !Array.isArray(cliente.pagos)) return;
      
      cliente.pagos.forEach(pago => {
        if (pago?.montosServicios && Array.isArray(pago.montosServicios)) {
          pago.montosServicios.forEach(monto => {
            const serviceId = monto?.servicioId?.servicio;
            if (!serviceId) return;
            
            const servicio = services.find(s => s.id === serviceId);
            if (!servicio) return;
            
            // Usar la categoría del servicio o "Otros" si no tiene
            const servicioCategory = servicio.category || "Otros";
            
            if (servicioCategory === categoria) {
              const montoValue = monto.monto || 0;
              montosPorCategoria[categoria].total += montoValue;
              
              // Si tiene IGV, suma el 18%
              if (monto.igv === "si") {
                montosPorCategoria[categoria].igvTotal += montoValue * 0.18;
              }
            }
          });
        }
      });
    });
  });

  // Filtrar categorías con montos mayores a 0
  const categoriasConMonto = categorias.filter(categoria => montosPorCategoria[categoria]?.total > 0);

  // Total de montos por categorías visibles (base para %)
  const totalCategoriasMonto = categoriasConMonto.reduce((acc, categoria) => acc + (montosPorCategoria[categoria]?.total || 0), 0);

  // Preprocesar los montos por servicio para cada cliente
  const montosPorServicio = record.reduce((acc, cliente) => {
    cliente.pagos.forEach(payment => {
      if (payment?.montosServicios && Array.isArray(payment.montosServicios)) {
        payment.montosServicios.forEach(servicio => {
          const serviceId = servicio?.servicioId?.servicio;
          if (!serviceId) return;

          if (!acc[serviceId]) {
            acc[serviceId] = {
              total: 0,
              igvTotal: 0
            };
          }

          const monto = servicio.monto || 0;
          acc[serviceId].total += monto;

          // Si tiene IGV, suma el 18%
          if (servicio.igv === "si") {
            acc[serviceId].igvTotal += monto * 0.18;
          }
        });
      }
    });
    return acc;
  }, {});

  //Calcular el total de dinero ingresado por cada servicio (para el gráfico)
  const totalPorServicio = services.map((s) => {
    const serviceData = montosPorServicio[s.id] || { total: 0, igvTotal: 0 };
    return {
      servicio: s.name,
      total: serviceData.total,
      igvTotal: serviceData.igvTotal
    };
  });

  // Calcular el total de dinero ingresado por cada categoría (para el gráfico)
  const totalPorCategoria = ServicesCategories.map((categoria) => {
    const categoriaData = montosPorCategoria[categoria.value] || { total: 0, igvTotal: 0 };
    return {
      categoria: categoria.label,
      total: categoriaData.total || 0,
      igvTotal: categoriaData.igvTotal || 0
    };
  }).filter(c => c.total > 0); // Solo mostrar categorías con ingresos

  // Debug: verificar datos
  // console.log("=== DEBUG CATEGORÍAS ===");
  // console.log("totalPorCategoria:", totalPorCategoria);
  // console.log("record length:", record.length);
  // console.log("services length:", services.length);
  // console.log("montosPorCategoria:", montosPorCategoria);
  // if (record.length > 0 && record[0].pagos) {
  //   console.log("Primer cliente pagos:", record[0].pagos.length);
  //   if (record[0].pagos[0]?.montosServicios) {
  //     console.log("Primer pago montosServicios:", record[0].pagos[0].montosServicios);
  //   }
  // }

  //Calcular la cantidad de cotiozaciones nuevas por mes
  const prepareQuotationData = () => {
    if (!quotations || quotations.length === 0) return [];

    // Agrupar cotizaciones por fecha
    const quotationsByDate = quotations.reduce((acc, quotation) => {
      const date = new Date(quotation.fechaVenta).toLocaleDateString();
      
      if (!acc[date]) {
        acc[date] = {
          fecha: date,
          cantidad: 0,
          montoTotal: 0
        };
      }
      
      acc[date].cantidad += 1;
      acc[date].montoTotal += quotation.precioTotal || 0;
      
      return acc;
    }, {});

    // Convertir a array y ordenar por fecha
    return Object.values(quotationsByDate)
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  };

  const prepareServiceData = () => {
    const serviceGroups = {};
    
    record.forEach(cliente => {
      cliente.pagos.forEach(pago => {
        if (pago?.montosServicios && Array.isArray(pago.montosServicios)) {
          pago.montosServicios.forEach(servicio => {
            const serviceId = servicio?.servicioId?.servicio;
            if (!serviceId) return;
            
            const servicioCompleto = services.find(s => s.id === serviceId);
            const serviceName = servicioCompleto?.name || 'Sin nombre';
            const serviceType = servicioCompleto?.tipoPago || 'Sin tipo';
            
            if (!serviceGroups[serviceName]) {
              serviceGroups[serviceName] = {
                name: serviceName,
                tipos: {
                  "Unico": 0,
                  "Mensual": 0,
                  "Anual": 0
                }
              };
            }
            
            if (serviceType) {
              serviceGroups[serviceName].tipos[serviceType] = (serviceGroups[serviceName].tipos[serviceType] || 0) + 1;
            }
          });
        }
      });
    });

    return Object.values(serviceGroups).map(service => ({
      name: service.name,
      ...service.tipos
    }));
  }

  const globalDefs = [
    {
      id: 'dots',
      type: 'patternDots',
      background: 'inherit',
      color: 'rgba(255, 255, 255, 0.3)',
      size: 4,
      padding: 1,
      stagger: true
    },
    {
      id: 'lines',
      type: 'patternLines',
      background: 'inherit',
      color: 'rgba(255, 255, 255, 0.3)',
      rotation: -45,
      lineWidth: 6,
      spacing: 10
    },
    {
      id: 'squares',
      type: 'patternSquares',
      background: 'inherit',
      color: 'rgba(255, 255, 255, 0.3)',
      rotation: 0,
      lineWidth: 50,
      spacing: 50
    }
  ]

  const chartTheme = {
    axis: {
      ticks: {
        text: {
          fill: "#666666",
          fontSize: 12
        }
      },
      legend: {
        text: {
          fill: "#666666",
          fontSize: 12
        }
      }
    },
    legends: {
      text: {
        fill: "#666666",
        fontSize: 12
      }
    }
  };

  return (
    <div className="flex flex-col h-screen w-full">
      
      {/* NavBar */}
      <div className="flex flex-row px-4 py-2 bg-gray-100">
        <div className="flex flex-row w-full items-center gap-4 justify-between overflow-x-auto overflow-y-hidden px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          
          <div className="flex items-center gap-4 py-4">
            <FaChartPie className="text-zinc-700 text-2xl" />
            <h2 className="text-2xl font-bold text-zinc-700">Estadística ingresos</h2>
          </div>

          <SearchBar
            value={brandName}
            getter={getRecord}
            setter={setBrandName}
            placeholder="Buscar marca"
            text="Buscar"
            disabled={loadingQuotations || loadingPayments || loadingServices}
            icon={<SearchIcon />}
          />

        </div>
      </div>

      {/* Barra de filtros */}
      <div className="flex bg-gray-100 mt-0.5 mb-0.5 justify-between w-full gap-10">
        <div className="flex flex-row w-full items-center justify-center overflow-x-auto gap-4 px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex flex-row items-center justify-between gap-4 px-4 ">
            {/* Fechas */}
            <div className="flex flex-row gap-4 items-center justify-start w-full px-4">
              <Selector
                title="Mes"
                value={month}
                setter={setMonth}
                options={months}
                disabled={loadingQuotations || loadingPayments || loadingServices}
              />
              <Selector
                title="Año"
                value={year}
                setter={setYear}
                options={years}
                disabled={loadingQuotations || loadingPayments || loadingServices}
              />
            </div>  
          </div>
        </div>
      </div>

      {/* Barra de Metadata */}
      <div className="flex bg-gray-100 mt-0.5 mb-0.5 justify-between w-full gap-10">
        <div className="flex flex-row w-full items-center justify-center overflow-x-auto gap-4 px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex flex-row items-center justify-between gap-4 px-4 ">

            {/* Servicios */}
            <div className="flex flex-row gap-4 items-center justify-start w-full px-4">
              
            </div>  

          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-grow overflow-y-auto">

        {/* 1 grafico detalle de montos*/}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 mt-4">

          {/* Montos por servicio*/}
          <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200" style={{ height: '600px' }}>
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Montos por Servicio</h3>
            <ResponsivePie
              data={(() => {
                const totalGeneral = totalPorServicio.reduce((sum, s) => sum + s.total, 0);
                return totalPorServicio
                  .map(s => ({ 
                    id: s.servicio, 
                    label: s.servicio, 
                    value: (s.total / totalGeneral) * 100 > 0 ? Number(((s.total / totalGeneral) * 100).toFixed(2)) : 0
                  }))
                  .filter(s => s.value > 0);
              })()}
              margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
              innerRadius={0.5}
              padAngle={0.7}
              cornerRadius={3}
              colors={{ scheme: 'nivo' }}
              borderWidth={1}
              borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
              radialLabelsSkipAngle={10}
              radialLabelsTextColor="#333333"
              radialLabelsLinkColor={{ from: 'color' }}
              sliceLabelsSkipAngle={10}
              sliceLabelsTextColor="#333333"
              tooltip={({ datum }) => (
                <strong>
                  {datum.id}: {Number(datum.value).toFixed(2)}%
                </strong>
              )}
              animate={true}
            />
          </div>

          {/* Lista de montos por servicio */}
          <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200" style={{ height: '600px' }}>
            <h3 className="text-xs font-semibold text-gray-700 mb-4">Detalle de Montos</h3>
            <div className="overflow-y-auto h-[calc(100%-2rem)]">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500">Servicio</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">Monto Total {totalPorServicio.length > 0 ? `(S/ ${totalPorServicio.reduce((sum, s) => sum + s.total, 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : ''}</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">Porcentaje</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">IGV {totalPorServicio.length > 0 ? `(S/ ${totalPorServicio.reduce((sum, s) => sum + s.igvTotal, 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : ''}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(() => {
                    const serviciosConMonto = totalPorServicio.filter(s => s.total > 0).sort((a, b) => b.total - a.total);
                    const totalGeneral = serviciosConMonto.reduce((sum, s) => sum + s.total, 0);
                    
                    return serviciosConMonto.map((service, index) => {
                      const porcentaje = totalGeneral > 0 ? ((service.total / totalGeneral) * 100) : 0;
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-[10px] whitespace-nowrap text-gray-900">{service.servicio}</td>
                          <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${service.total === 0 ? 'text-gray-200' : 'text-black'}`}>S/ {service.total.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${porcentaje === 0 ? 'text-gray-200' : 'text-black font-semibold'}`}>{porcentaje.toFixed(2)}%</td>
                          <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${service.igvTotal === 0 ? 'text-gray-200' : 'text-black'}`}>S/ {service.igvTotal.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 2 grafico detalle por categorías*/}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 mt-4">

          {/* Montos por categoría*/}
          <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200" style={{ height: '600px' }}>
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Ingresos por Categoría</h3>
            {totalPorCategoria.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500 text-sm">No hay datos disponibles para el período seleccionado</p>
              </div>
            ) : (
            <ResponsivePie
              data={(() => {
                if (!totalPorCategoria || totalPorCategoria.length === 0) {
                  return [];
                }
                const totalGeneral = totalPorCategoria.reduce((sum, c) => sum + (c.total || 0), 0);
                if (totalGeneral === 0) {
                  return [];
                }
                return totalPorCategoria
                  .map(c => ({ 
                    id: c.categoria, 
                    label: c.categoria, 
                    value: Number(((c.total / totalGeneral) * 100).toFixed(2))
                  }))
                  .filter(c => c.value > 0);
              })()}
              margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
              innerRadius={0.5}
              padAngle={0.7}
              cornerRadius={3}
              colors={{ scheme: 'nivo' }}
              borderWidth={1}
              borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
              radialLabelsSkipAngle={10}
              radialLabelsTextColor="#333333"
              radialLabelsLinkColor={{ from: 'color' }}
              sliceLabelsSkipAngle={10}
              sliceLabelsTextColor="#333333"
              tooltip={({ datum }) => {
                const categoriaData = totalPorCategoria.find(c => c.categoria === datum.id);
                return (
                  <div className="bg-white p-2 border border-gray-200 rounded shadow">
                    <strong>{datum.id}</strong>
                    <br />
                    <span>Porcentaje: {Number(datum.value).toFixed(2)}%</span>
                    <br />
                    <span>Monto: S/ {categoriaData?.total?.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 0}</span>
                  </div>
                );
              }}
              animate={true}
            />
            )}
          </div>

          {/* Lista de montos por categoría */}
          <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200" style={{ height: '600px' }}>
            <h3 className="text-xs font-semibold text-gray-700 mb-4">Detalle de Ingresos por Categoría</h3>
            {totalPorCategoria.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500 text-sm">No hay datos disponibles para el período seleccionado</p>
              </div>
            ) : (
            <div className="overflow-y-auto h-[calc(100%-2rem)]">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500">Categoría</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">Monto Total {totalPorCategoria.length > 0 ? `(S/ ${totalPorCategoria.reduce((sum, c) => sum + c.total, 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : ''}</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">Porcentaje</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">IGV {totalPorCategoria.length > 0 ? `(S/ ${totalPorCategoria.reduce((sum, c) => sum + c.igvTotal, 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : ''}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(() => {
                    const categoriasOrdenadas = totalPorCategoria.sort((a, b) => b.total - a.total);
                    const totalGeneral = categoriasOrdenadas.reduce((sum, c) => sum + c.total, 0);
                    
                    return categoriasOrdenadas.map((categoria, index) => {
                      const porcentaje = totalGeneral > 0 ? ((categoria.total / totalGeneral) * 100) : 0;
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-[10px] whitespace-nowrap text-gray-900">{categoria.categoria}</td>
                          <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${categoria.total === 0 ? 'text-gray-200' : 'text-black'}`}>S/ {categoria.total.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${porcentaje === 0 ? 'text-gray-200' : 'text-black font-semibold'}`}>{porcentaje.toFixed(2)}%</td>
                          <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${categoria.igvTotal === 0 ? 'text-gray-200' : 'text-black'}`}>S/ {categoria.igvTotal.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
            )}
          </div>
        </div>

        {/* 3 grafico servicios por tipo*/}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 mt-4">
          <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200" style={{ height: '600px' }}>
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Servicios por tipo</h3>
            <ResponsivePie
              data={(() => {
                const serviceData = prepareServiceData();
                return serviceData.flatMap(service => [
                  {
                    id: `${service.name} (Único)`,
                    label: `${service.name} (Único)`,
                    value: service.Unico || 0,
                    tipo: 'Único'
                  },
                  {
                    id: `${service.name} (Mensual)`,
                    label: `${service.name} (Mensual)`,
                    value: service.Mensual || 0,
                    tipo: 'Mensual'
                  },
                  {
                    id: `${service.name} (Anual)`,
                    label: `${service.name} (Anual)`,
                    value: service.Anual || 0,
                    tipo: 'Anual'
                  }
                ]).filter(s => s.value > 0);
              })()}
              margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
              innerRadius={0.5}
              padAngle={0.7}
              cornerRadius={3}
              colors={{ scheme: 'nivo' }}
              borderWidth={1}
              borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
              radialLabelsSkipAngle={10}
              radialLabelsTextColor="#333333"
              radialLabelsLinkColor={{ from: 'color' }}
              sliceLabelsSkipAngle={10}
              sliceLabelsTextColor="#333333"
              tooltip={({ datum }) => (
                <strong>
                  {datum.id}: {datum.value} servicios
                </strong>
              )}
              animate={true}
            />
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200" style={{ height: '600px' }}>
            <h3 className="text-xs font-semibold text-gray-700 mb-4">Detalle de Servicios</h3>
            <div className="overflow-y-auto h-[calc(100%-2rem)]">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500">Servicio</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">Único</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">Mensual</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">Anual</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {prepareServiceData()
                    .sort((a, b) => {
                      const totalA = (a.Unico || 0) + (a.Mensual || 0) + (a.Anual || 0);
                      const totalB = (b.Unico || 0) + (b.Mensual || 0) + (b.Anual || 0);
                      return totalB - totalA;
                    })
                    .map((service, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-[10px] whitespace-nowrap text-gray-900">{service.name}</td>
                        <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${service.Unico === 0 ? 'text-gray-200' : 'text-black'}`}>{service.Unico || 0}</td>
                        <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${service.Mensual === 0 ? 'text-gray-200' : 'text-black'}`}>{service.Mensual || 0}</td>
                        <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${service.Anual === 0 ? 'text-gray-200' : 'text-black'}`}>{service.Anual || 0}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div> */}

        {/* 3 gráfico vendidos por fecha*/}
        <div className="flex items-center justify-center w-full mt-4 px-4">
          <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200 w-full" style={{ height: '600px' }}>
            <div className="flex flex-row justify-between items-center">
              <h3 className="text-xs font-semibold text-gray-700">Servicios vendidos por fecha</h3>
            </div>
            <ResponsiveBar
              data={prepareDailyChartData()}
              keys={getServiceTypes()}
              indexBy="fecha"
              margin={{ top: 50, right: 130, bottom: 80, left: 60 }}
              padding={0.3}
              valueScale={{ type: 'linear' }}
              indexScale={{ type: 'band', round: true }}
              colors={{ scheme: 'paired' }}
              groupMode="stacked"
              borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              axisTop={null}
              axisRight={null}
              defs={globalDefs}
              fill={[
                {
                  match: {
                    id: getServiceTypes()[0]
                  },
                  id: 'dots'
                },
                {
                  match: {
                    id: getServiceTypes()[1]
                  },
                  id: 'lines'
                },
                {
                  match: {
                    id: getServiceTypes()[2]
                  },
                  id: 'squares'
                }
              ]}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: -25,
                legend: 'Fecha',
                legendPosition: 'middle',
                legendOffset: 48
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Cantidad de Servicios',
                legendPosition: 'middle',
                legendOffset: -40
              }}
              labelSkipWidth={12}
              labelSkipHeight={12}
              labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              legends={[
                {
                  dataFrom: 'keys',
                  anchor: 'bottom-right',
                  direction: 'column',
                  justify: false,
                  translateX: 120,
                  translateY: 0,
                  itemsSpacing: 2,
                  itemWidth: 100,
                  itemHeight: 20,
                  itemDirection: 'left-to-right',
                  itemOpacity: 0.85,
                  symbolSize: 20,
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemOpacity: 1
                      }
                    }
                  ]
                }
              ]}
              theme={chartTheme}
              animate={true}
              motionConfig="gentle"
            />
          </div>
        </div>

        {/* 4 grafico cotizaciones a clientes nuevos*/}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 mt-4">
          {/* Estadísticas de Cotizaciones */}
          {/* <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200" style={{ height: '600px' }}>
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Estadísticas de Cotizaciones a clientes nuevos</h3>
            <ResponsiveBar
              data={prepareQuotationData()}
              keys={['cantidad']}
              indexBy="fecha"
              margin={{ top: 50, right: 130, bottom: 80, left: 60 }}
              padding={0.3}
              valueScale={{ type: 'linear' }}
              indexScale={{ type: 'band', round: true }}
              colors={{ scheme: 'nivo' }}
              borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: -25,
                legend: 'Fecha',
                legendPosition: 'middle',
                legendOffset: 48
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Cantidad de Cotizaciones "Primera cotización"',
                legendPosition: 'middle',
                legendOffset: -40
              }}
              labelSkipWidth={12}
              labelSkipHeight={12}
              labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              tooltip={({ data }) => (
                <div className="bg-white p-2 border border-gray-200 rounded shadow">
                  <strong>Fecha: {data.fecha}</strong>
                  <br />
                  <span>Cantidad: {data.cantidad}</span>
                  <br />
                  <span>Monto Total: S/ {data?.precioTotal?.toFixed(2)}</span>
                </div>
              )}
              theme={chartTheme}
              animate={true}
              motionConfig="gentle"
            />
          </div> */}

          {/* Resumen de Cotizaciones */}
          {/* <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200" style={{ height: '600px' }}>
            <h3 className="text-xs font-semibold text-gray-700 mb-4">Resumen de Cotizaciones</h3>
            <div className="overflow-y-auto h-[calc(100%-2rem)]">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500">Fecha</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">Cantidad</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">Monto Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {prepareQuotationData()
                    .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
                    .map((data, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-[10px] whitespace-nowrap text-gray-900">{data.fecha}</td>
                        <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${data.cantidad === 0 ? 'text-gray-200' : 'text-black'}`}>{data.cantidad}</td>
                        <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${data.montoTotal === 0 ? 'text-gray-200' : 'text-black'}`}>S/ {data.montoTotal.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div> */}
        </div>

        {/* 5 grafico servicios segmentados por tipo de renovacion*/}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 mt-4">
          <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200" style={{ height: '600px' }}>
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Servicios por Tipo de Renovación</h3>
            <ResponsivePie
              data={(() => {
                const serviceData = prepareServiceData();
                const totalUnico = serviceData.reduce((sum, s) => sum + (s.Unico || 0), 0);
                const totalMensual = serviceData.reduce((sum, s) => sum + (s.Mensual || 0), 0);
                const totalAnual = serviceData.reduce((sum, s) => sum + (s.Anual || 0), 0);

                return [
                  {
                    id: 'Único',
                    label: 'Único',
                    value: totalUnico
                  },
                  {
                    id: 'Mensual',
                    label: 'Mensual',
                    value: totalMensual
                  },
                  {
                    id: 'Anual',
                    label: 'Anual',
                    value: totalAnual
                  }
                ].filter(s => s.value > 0);
              })()}
              margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
              innerRadius={0.5}
              padAngle={0.7}
              cornerRadius={3}
              colors={{ scheme: 'nivo' }}
              borderWidth={1}
              borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
              radialLabelsSkipAngle={10}
              radialLabelsTextColor="#333333"
              radialLabelsLinkColor={{ from: 'color' }}
              sliceLabelsSkipAngle={10}
              sliceLabelsTextColor="#333333"
              tooltip={({ datum }) => (
                <strong>
                  {datum.label}: {datum.value} servicios
                </strong>
              )}
              animate={true}
            />
          </div>

          <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200" style={{ height: '600px' }}>
            <h3 className="text-xs font-semibold text-gray-700 mb-4">Cantidad de Servicios por Tipo</h3>
            <div className="overflow-y-auto h-[calc(100%-2rem)]">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500">Servicio</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">Único</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">Mensual</th>
                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500">Anual</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {prepareServiceData()
                    .sort((a, b) => {
                      const totalA = (a.Unico || 0) + (a.Mensual || 0) + (a.Anual || 0);
                      const totalB = (b.Unico || 0) + (b.Mensual || 0) + (b.Anual || 0);
                      return totalB - totalA;
                    })
                    .map((service, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-[10px] whitespace-nowrap text-gray-900">{service.name}</td>
                        <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${service.Unico === 0 ? 'text-gray-200' : 'text-black'}`}>{service.Unico || 0}</td>
                        <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${service.Mensual === 0 ? 'text-gray-200' : 'text-black'}`}>{service.Mensual || 0}</td>
                        <td className={`px-4 py-2 text-[10px] whitespace-nowrap text-right ${service.Anual === 0 ? 'text-gray-200' : 'text-black'}`}>{service.Anual || 0}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div> */}

      </div>

    </div>
  )
}

export default EstadisticaIngresos;